import * as React from 'react';
import TradePage from '../../../common/components/TradePage/TradePage';
import config from '../../../store/mmd/config';
import { IFindOpenLoanUIProps } from './FindOpenLoanInterfaces';
import { Select, Input } from '@makemydeal/ui-bricks/dist/cox';
import { SpinnerUI } from '@makemydeal/dr-activities-common';
import { IPayoffQuoteResponse } from '../../../types';
import * as tradeInRoutes from '../../../utils/routes';

const FIELD_NAME = {
    LENDER_NAME: 'lenderName',
    LOAN_NUMBER: 'loanNumber',
    CUSTOMER_LAST_NAME: 'customerLastName',
    SSN: 'ssn',
    VIN: 'vin'
} as const;

const isVINFieldDisabled = true;
const ssnRegex = /^\d{4}$/;

export const FindOpenLoanUI: React.FC<IFindOpenLoanUIProps> = (props) => {
    const {
        pageTitle,
        pageSubTitle,
        stepperRoutes,
        goToRoute,
        dispatchAnalytics,
        fetchLienholders,
        fetchLienholdersOptions,
        postPayoffQuote,
        onEstimateBalanceClick,
        goBack,
        removeTradeIn,
        isFetchingLienholders,
        isFetchingLienholdersOptions,
        isPayoffQuoteInProgress,
        lienholdersData,
        lienholdersOptionsData,
        payoffQuoteData,
        cdlTradeInProgressStepperClicked,
        saveAmountOwed,
        saveAmountOwedSource,
        vin,
        clearPayoffQuoteData,
        cdlContinueButtonClicked,
        urlPath
    } = props;

    const [isFormValid, setIsFormValid] = React.useState(false);
    const [isFormSubmitted, setIsFormSubmitted] = React.useState(false);
    const [maskedSSN, setMaskedSSN] = React.useState('');
    const [actualSSN, setActualSSN] = React.useState('');
    const [shouldHideChar, setShouldHideChar] = React.useState(true);

    const [formData, setFormData] = React.useState({
        [FIELD_NAME.LENDER_NAME]: '',
        [FIELD_NAME.LOAN_NUMBER]: '',
        [FIELD_NAME.CUSTOMER_LAST_NAME]: '',
        [FIELD_NAME.SSN]: '',
        [FIELD_NAME.VIN]: vin
    });

    const shouldDisplayField = !isFetchingLienholdersOptions && formData[FIELD_NAME.LENDER_NAME] !== '';

    React.useEffect(() => {
        fetchLienholders();
    }, [fetchLienholders]);

    React.useEffect(() => {
        if (!isFormSubmitted || isPayoffQuoteInProgress) {
            return;
        }

        if (payoffQuoteData && Object.keys(payoffQuoteData).length > 0) {
            const result = (payoffQuoteData as IPayoffQuoteResponse)?.payoffQuoteResponse?.[0];
            const payoffAmount = result?.finance?.payoffAmount;
            const payoffMsg = result?.payoffMsg;
            const partnerId = result?.finance?.partnerId;

            if (partnerId && partnerId !== formData[FIELD_NAME.LENDER_NAME]) {
                return;
            }

            if (payoffAmount) {
                saveAmountOwed(Number(payoffAmount));
                const lenderName =
                    lienholdersData.find((lienholder) => lienholder.partnerId === formData[FIELD_NAME.LENDER_NAME])?.name || '';
                saveAmountOwedSource(lenderName);
                goToRoute(tradeInRoutes.TRADE_LOAN_CONFIRMATION);
            } else {
                goToRoute(tradeInRoutes.TRADE_AUTO_LOAN_UNAVAILABLE);
            }
            setIsFormSubmitted(false);
        }
    }, [payoffQuoteData, isPayoffQuoteInProgress, isFormSubmitted, goToRoute]);

    const renderSelectOptions = React.useCallback(() => {
        return (
            <>
                {lienholdersData.map(({ partnerId, name }) => (
                    <option key={partnerId} value={partnerId}>
                        {name}
                    </option>
                ))}
            </>
        );
    }, [lienholdersData]);

    const getSearchConfig = React.useCallback(() => {
        const retailConfig = lienholdersOptionsData;
        if (!lienholdersOptionsData || lienholdersOptionsData?.length === 0) {
            return undefined;
        }

        const retailVinConfig = lienholdersOptionsData.find((config) => config.keyId === 'VIN');
        return retailVinConfig ?? retailConfig[0];
    }, [lienholdersOptionsData]);

    const searchConfig = getSearchConfig();
    const shouldShowLoanNumber = searchConfig && searchConfig?.keyId === 'ACC';
    const shouldShowCustomerLastName = searchConfig && searchConfig?.lastNameRequired;
    const shouldShowSSN = searchConfig && searchConfig?.ssnLast4Required;

    const validateSSN = React.useCallback(
        (_value: string): boolean => {
            if (!shouldShowSSN) {
                return true;
            }
            return ssnRegex.test(formData[FIELD_NAME.SSN]);
        },
        [shouldShowSSN, formData[FIELD_NAME.SSN]]
    );

    const validateCustomerLastName = React.useCallback(
        (value: string): boolean => {
            if (!shouldShowCustomerLastName) {
                return true;
            }
            return value.length > 2 && value.length <= 20;
        },
        [shouldShowCustomerLastName]
    );

    const validateLoanNumber = React.useCallback(
        (value: string): boolean => {
            if (!shouldShowLoanNumber) {
                return true;
            }
            const length = searchConfig?.length || searchConfig?.minLength || 0;
            return value.length === length;
        },
        [shouldShowLoanNumber, searchConfig]
    );

    const handleSSNChange = (e) => {
        const currentValue = e.currentTarget.value;
        const prevMaskedLength = maskedSSN.length;
        const currentLength = currentValue.length;

        if (!shouldHideChar) {
            const numericValue = currentValue.replace(/\D/g, '').slice(0, 4);
            setActualSSN(numericValue);
            setMaskedSSN('*'.repeat(numericValue.length));
            setFormData({ ...formData, [FIELD_NAME.SSN]: numericValue });
            return;
        }

        if (currentLength < prevMaskedLength) {
            const newActualSSN = actualSSN.slice(0, currentLength);
            setActualSSN(newActualSSN);
            setMaskedSSN('*'.repeat(newActualSSN.length));
            setFormData({ ...formData, [FIELD_NAME.SSN]: newActualSSN });
            return;
        }

        const newChars = currentValue.slice(prevMaskedLength);
        const numericNewChars = newChars.replace(/\D/g, '');

        if (numericNewChars.length === 0) {
            e.currentTarget.value = maskedSSN;
            return;
        }

        const newActualSSN = (actualSSN + numericNewChars).slice(0, 4);
        setActualSSN(newActualSSN);
        setMaskedSSN('*'.repeat(newActualSSN.length));
        setFormData({ ...formData, [FIELD_NAME.SSN]: newActualSSN });
    };

    const checkFormValidity = React.useCallback(() => {
        if (!searchConfig) {
            return false;
        }

        if (!formData[FIELD_NAME.LENDER_NAME]) {
            return false;
        }

        if (shouldShowLoanNumber && !validateLoanNumber(formData[FIELD_NAME.LOAN_NUMBER])) {
            return false;
        }

        if (shouldShowCustomerLastName && !validateCustomerLastName(formData[FIELD_NAME.CUSTOMER_LAST_NAME])) {
            return false;
        }

        if (shouldShowSSN && !validateSSN(formData[FIELD_NAME.SSN])) {
            return false;
        }

        return true;
    }, [
        formData,
        searchConfig,
        shouldShowLoanNumber,
        shouldShowCustomerLastName,
        shouldShowSSN,
        validateLoanNumber,
        validateCustomerLastName,
        validateSSN
    ]);

    React.useEffect(() => {
        setIsFormValid(checkFormValidity());
    }, [checkFormValidity]);

    const handleChange = (e) => {
        const { currentTarget } = e;
        const key = currentTarget.name;
        const value = currentTarget.value;

        setFormData({ ...formData, [key]: value });
    };

    const handleToggleHideChar = () => {
        setShouldHideChar(!shouldHideChar);
    };

    const handleFindMyOpenAutoLoanClick = () => {
        const searchKey = searchConfig?.keyId || 'VIN';
        const searchValue = formData[searchKey === 'VIN' ? FIELD_NAME.VIN : FIELD_NAME.LOAN_NUMBER];
        const partnerId = formData[FIELD_NAME.LENDER_NAME];

        const queryParams = new URLSearchParams();
        queryParams.append('partnerId', partnerId);
        queryParams.append('searchKey', searchKey);
        queryParams.append('searchValue', searchValue);

        if (shouldShowCustomerLastName) {
            queryParams.append('customerLastName', formData[FIELD_NAME.CUSTOMER_LAST_NAME]);
        }

        if (shouldShowSSN) {
            queryParams.append('ssnLast4', formData[FIELD_NAME.SSN]);
        }

        const queryString = queryParams.toString();
        cdlContinueButtonClicked();
        postPayoffQuote(queryString);
        setIsFormSubmitted(true);
    };

    const handleEstimateBalanceClick = () => {
        onEstimateBalanceClick();
    };

    const handleSelectOnChange: React.ChangeEventHandler<HTMLSelectElement> = async (event): Promise<void> => {
        const { value } = event.currentTarget;

        if (!value) {
            return;
        }

        clearPayoffQuoteData();
        setFormData({ ...formData, [FIELD_NAME.LENDER_NAME]: value });

        const fetchLienholdersOptionsResult = await fetchLienholdersOptions(value);
    };

    const getErrorMessage = (
        fieldName: string,
        shouldValidate?: boolean,
        minLength?: number,
        maxLength?: number,
        regex?: RegExp
    ): string => {
        let errorMessage = '';
        const value = formData[fieldName];
        const isFixedLength = searchConfig && searchConfig.length !== undefined;

        if (!shouldValidate) {
            return errorMessage;
        }

        if (!value) {
            errorMessage = 'This field is required';
        } else if (value.length < (minLength || 0)) {
            if (fieldName === FIELD_NAME.SSN) {
                errorMessage = 'SSN must be exactly 4 digits';
                return errorMessage;
            }

            if (isFixedLength && fieldName === FIELD_NAME.LOAN_NUMBER) {
                errorMessage = `Please enter a valid value with exactly last ${minLength} numbers`;
            } else {
                errorMessage = `Please enter a valid value with at least ${minLength} characters`;
            }
        } else if (maxLength && value.length > maxLength) {
            errorMessage = `Please enter a valid value with at most ${maxLength} characters`;
        } else if (regex && !regex.test(value)) {
            errorMessage = 'Please enter valid format';
        }
        return errorMessage;
    };

    return (
        <TradePage
            headerOnTop
            showCustomTitle
            pageClass="find-open-loan-page"
            pageTitle={pageTitle}
            pageSubTitle={pageSubTitle}
            goToRoute={goToRoute}
            dispatchAnalytics={dispatchAnalytics}
            footerProps={{
                showBackButton: true,
                onBackButtonClick: goBack,
                disableActionButton: false,
                fullWidthLayout: true,
                showSkipButton: true,
                skipButtonText: 'Cancel Trade-in',
                onSkipButtonClick: removeTradeIn
            }}
            stepperRoutes={stepperRoutes}
            showTradeErrorMessage={false}
            staticAssetsConfig={config.staticImages}
            stepLabelVisible={true}
            isStepperClickable={true}
            cdlPageProgressStepperClicked={cdlTradeInProgressStepperClicked}
            urlPath={urlPath}
        >
            <div className="find-open-loan-container">
                <div className="find-open-loan-header">
                    <p className="title">Let's find your open auto loan</p>
                </div>
                <div className="row-responsive">
                    <div className="responsive">
                        <Select
                            selectId={FIELD_NAME.LENDER_NAME}
                            name={FIELD_NAME.LENDER_NAME}
                            value={formData[FIELD_NAME.LENDER_NAME]}
                            defaultValue=""
                            label="Lender Name"
                            onChangeFunction={handleSelectOnChange}
                        >
                            <option key="select-lender" value="">
                                Select a Lender
                            </option>
                            {renderSelectOptions()}
                        </Select>
                        <div className="info-div">
                            <p>
                                If you don't see your lender listed, you can{' '}
                                <a
                                    data-testid="estimate-loan-balance-link"
                                    className="primary-link"
                                    onClick={handleEstimateBalanceClick}
                                >
                                    estimate your loan balance.{' '}
                                </a>
                            </p>
                        </div>
                        {(isFetchingLienholders || isPayoffQuoteInProgress) && (
                            <div className="spinner-overlay">
                                <SpinnerUI size="30" />
                            </div>
                        )}
                    </div>
                    {shouldDisplayField && (
                        <div className="responsive">
                            <Input
                                type="text"
                                name={FIELD_NAME.VIN}
                                inputId="vehicle-vin"
                                label="VIN"
                                placeholder="Enter your vehicle VIN"
                                value={formData[FIELD_NAME.VIN].toString()}
                                onChangeFunction={handleChange}
                                data-testid="vehicle-vin-value"
                                disabled={isVINFieldDisabled}
                            />
                            <div className="info-div">
                                <span>Please verify your VIN</span>
                            </div>
                        </div>
                    )}
                </div>

                {isFetchingLienholdersOptions && (
                    <div className="spinner-overlay">
                        <SpinnerUI size="30" />
                    </div>
                )}

                {shouldDisplayField && (shouldShowLoanNumber || shouldShowCustomerLastName) && (
                    <div className="row-responsive">
                        {shouldShowLoanNumber && (
                            <div className="responsive">
                                <Input
                                    type="text"
                                    name={FIELD_NAME.LOAN_NUMBER}
                                    inputId="loan-number"
                                    label={`Loan Number ${searchConfig?.lastXOf && searchConfig?.length ? `(Last ${searchConfig.length} characters)` : ''}`}
                                    placeholder="Enter your loan number"
                                    value={formData[FIELD_NAME.LOAN_NUMBER].toString()}
                                    onChangeFunction={handleChange}
                                    validationFunction={validateLoanNumber}
                                    data-testid="loan-number-value"
                                    errorMessage={getErrorMessage(
                                        FIELD_NAME.LOAN_NUMBER,
                                        shouldShowLoanNumber,
                                        searchConfig?.length ?? searchConfig?.minLength ?? 0,
                                        searchConfig?.length ?? searchConfig?.maxLength
                                    )}
                                />
                            </div>
                        )}
                        {shouldShowCustomerLastName && (
                            <div className="responsive">
                                <Input
                                    type="text"
                                    name={FIELD_NAME.CUSTOMER_LAST_NAME}
                                    inputId="customer-last-name"
                                    label="Your Last Name"
                                    placeholder="Enter your last name"
                                    value={formData[FIELD_NAME.CUSTOMER_LAST_NAME].toString()}
                                    maxLength={20}
                                    onChangeFunction={handleChange}
                                    validationFunction={validateCustomerLastName}
                                    data-testid="customer-last-name-value"
                                    errorMessage={getErrorMessage(FIELD_NAME.CUSTOMER_LAST_NAME, shouldShowCustomerLastName, 2, 20)}
                                />
                            </div>
                        )}
                    </div>
                )}

                {shouldDisplayField && shouldShowSSN && (
                    <div className="row-responsive">
                        <div className="responsive">
                            <Input
                                type="text"
                                name={FIELD_NAME.SSN}
                                inputId="customer-ssn"
                                label="Last 4 Digits of Social Security Number"
                                placeholder="Enter your SSN"
                                value={shouldHideChar ? maskedSSN : actualSSN}
                                onChangeFunction={handleSSNChange}
                                validationFunction={validateSSN}
                                data-testid="customer-ssn-value"
                                maxLength={4}
                                minLength={4}
                                errorMessage={getErrorMessage(FIELD_NAME.SSN, shouldShowSSN, 4, 4, ssnRegex)}
                            />
                            <i
                                className={`fa ${shouldHideChar ? 'fa-eye-slash' : 'fa-eye'} ssn-icon`}
                                onClick={handleToggleHideChar}
                            />
                        </div>
                    </div>
                )}

                <div className="find-my-loan-button-container">
                    <button
                        data-testid="find-my-loan-button"
                        className="btn btn-primary"
                        onClick={handleFindMyOpenAutoLoanClick}
                        disabled={!isFormValid}
                    >
                        Continue
                    </button>
                </div>

                <p className="estimate-loan-balance-link">
                    You can also{' '}
                    <a data-testid="estimate-loan-balance-link" className="primary-link" onClick={handleEstimateBalanceClick}>
                        estimate your loan balance.{' '}
                    </a>
                    We'll confirm the exact amount before finalizing your purchase.
                </p>
            </div>
        </TradePage>
    );
};

export default FindOpenLoanUI;
